import React, { Suspense, useEffect } from 'react'
import {
    createTheme,
    ThemeProvider,
    StyledEngineProvider,
    responsiveFontSizes,
} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { newThemeV5 } from 'utilities/themeConfig'
import './scss/app.scss'
import { useAppSelector } from 'store/useStoreHook'
import SplashScreenPage from 'pages/SplashPage'
import init from './utilities/yupErrorMessage/yupErrorMessage'
import useOfflineTracker from 'views/Sale/contexts/useOfflineTracker'
import SWAppReload from 'AppRoot/SWAppReload'
import ApplyGlobalStyle from 'AppRoot/ApplyGlobalStyle'
import { usePostHog } from 'posthog-js/react'
import posthogIntegration from 'utilities/posthogIntegration'
import ResponseErrorDialog from 'components/Dialog/ResponseErrorDialog'
import LicenseExpiredDialog from 'components/LicenseExpiredDialog/LicenseExpiredDialog'
import getEnv, { isProduction } from 'utilities/getEnv'
import ErrorBoundaryPage from 'pages/ErrorBoundaryPage'
import { ErrorBoundary } from '@sentry/react'
import AppRoot from 'AppRoot/AppRoot'
import { useLocation } from 'react-router-dom'
import androidWebViewApi from 'utilities/androidWebViewApi'

const DynamicallyImportError = 'dynamically imported module'
const LOCAL_WITHOUT_PWA = getEnv('LOCAL_WITHOUT_PWA')

function App() {
    init()
    useOfflineTracker()

    const location = useLocation()
    const posthog = usePostHog()
    const currentUser = useAppSelector((s) => s.auth.user)
    const tenantIdentifierInfo = useAppSelector(
        (s) => s.tenantIdentifierInfo.data,
    )
    const client = useAppSelector((s) => s.client.currentClient)
    const isAuthExpired = useAppSelector((s) => s.auth.isAuthExpired)
    const isLoggedOut = useAppSelector((s) => s.auth.isLoggedOut)

    useEffect(() => {
        if (isProduction() && !(getEnv('SKIP_POSTHOG') ?? false)) {
            if (currentUser && client) {
                posthogIntegration.setIdentify(
                    posthog,
                    currentUser,
                    tenantIdentifierInfo,
                    client,
                )
            }
            // Reset posthog session if user is locked out or auth expired
            else if (isAuthExpired || isLoggedOut) {
                posthogIntegration.reset(posthog)
            }
        }
    }, [
        posthog,
        currentUser,
        tenantIdentifierInfo,
        client,
        isAuthExpired,
        isLoggedOut,
    ])

    useEffect(() => {
        if (isProduction() && !(getEnv('SKIP_POSTHOG') ?? false)) {
            posthogIntegration.setGroup(posthog, tenantIdentifierInfo)
        }
    }, [posthog, tenantIdentifierInfo])

    useEffect(() => {
        androidWebViewApi.onPathnameChanged(location.pathname)
    }, [location.pathname])

    const prefersLightMode = useAppSelector(
        (state) => state.ui.themeSetting.lightTheme,
    )

    const themes = React.useMemo(
        () =>
            responsiveFontSizes(
                createTheme(newThemeV5(prefersLightMode ? 'light' : 'dark')),
            ),
        [prefersLightMode],
    )

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <ErrorBoundary
                    fallback={({ error }) => {
                        if (
                            error.message
                                .toLocaleLowerCase()
                                .includes(DynamicallyImportError)
                        ) {
                            // Use blank page
                            return <div />
                        } else return <ErrorBoundaryPage />
                    }}
                    onError={(error) => {
                        if (
                            error.message
                                .toLocaleLowerCase()
                                .includes(DynamicallyImportError)
                        ) {
                            // Reload immediately
                            window.location.reload()
                        } else {
                            if (
                                isProduction() ||
                                getEnv('AUTO_RELOAD_WHEN_ERROR_ON_DEV_MODE')
                            ) {
                                setTimeout(function () {
                                    window.location.reload()
                                }, 5000)
                            }
                        }
                    }}
                >
                    <Suspense fallback={<SplashScreenPage />}>
                        <CssBaseline />
                        <ApplyGlobalStyle />
                        {LOCAL_WITHOUT_PWA ? <AppRoot /> : <SWAppReload />}
                        <ResponseErrorDialog />
                        <LicenseExpiredDialog />
                    </Suspense>
                </ErrorBoundary>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

export default App
